import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap,
  Send,
  RefreshCw,
  RotateCw,
  CheckCircle2,
  XCircle,
  Clock,
  Copy,
  Play,
  Eye,
  AlertTriangle,
  Users,
  Shield,
  FileText,
  Webhook,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Event type options
const CREDENTIALING_EVENTS = [
  { value: 'driver.credentialing.cleared', label: 'Credentialing Cleared', color: 'neon-green' },
  { value: 'driver.credentialing.in_review', label: 'In Review', color: 'yellow-500' },
  { value: 'driver.credentialing.rejected', label: 'Rejected', color: 'red-500' },
  { value: 'driver.credentialing.lapsed', label: 'Lapsed', color: 'orange-500' },
  { value: 'driver.credentialing.expiring_soon', label: 'Expiring Soon', color: 'amber-500' },
  { value: 'driver.credentialing.restored', label: 'Restored', color: 'neon-cyan' }
];

const INSURANCE_EVENTS = [
  { value: 'insurance.active', label: 'Policy Active', color: 'neon-green' },
  { value: 'insurance.declined', label: 'Policy Declined', color: 'red-500' },
  { value: 'insurance.expiring_soon', label: 'Expiring Soon', color: 'amber-500' },
  { value: 'insurance.expired', label: 'Expired', color: 'red-500' },
  { value: 'insurance.cancelled', label: 'Cancelled', color: 'orange-500' },
  { value: 'insurance.claim_filed', label: 'Claim Filed', color: 'neon-purple' }
];

// Status badge
const StatusBadge = ({ status }) => {
  const statusColors = {
    delivered: 'bg-neon-green/20 text-neon-green',
    simulated: 'bg-neon-cyan/20 text-neon-cyan',
    failed: 'bg-red-500/20 text-red-500',
    pending: 'bg-yellow-500/20 text-yellow-500'
  };
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[status] || 'bg-slate-600 text-slate-300'}`}>
      {status}
    </span>
  );
};

// Fire Event Modal
const FireEventModal = ({ isOpen, onClose, apiKey, onSuccess }) => {
  const [eventType, setEventType] = useState('driver.credentialing.cleared');
  const [applicationId, setApplicationId] = useState('');
  const [policyId, setPolicyId] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [customPayload, setCustomPayload] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const isCredentialingEvent = eventType.startsWith('driver.');
  const isInsuranceEvent = eventType.startsWith('insurance.');

  const handleFire = async () => {
    setLoading(true);
    setResult(null);
    
    try {
      const payload = {
        event_type: eventType,
        ...(applicationId && { application_id: applicationId }),
        ...(policyId && { policy_id: policyId }),
        ...(externalRef && { external_ref: externalRef }),
        ...(customPayload && { custom_payload: JSON.parse(customPayload) })
      };

      const response = await fetch(`${API_URL}/api/medexova/sandbox/fire-event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': apiKey
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      setResult(data);
      
      if (data.delivery_result?.success || data.delivery_result?.simulated) {
        toast.success(`Event fired: ${eventType}`);
        onSuccess?.();
      } else {
        toast.error('Event delivery failed');
      }
    } catch (error) {
      setResult({ error: error.message });
      toast.error('Failed to fire event');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="glass-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
          <Zap className="w-5 h-5 mr-2 text-neon-yellow" />
          Fire Webhook Event
        </h3>

        <div className="space-y-4">
          {/* Event Type */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block">Event Type</label>
            <select
              value={eventType}
              onChange={e => setEventType(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
            >
              <optgroup label="Credentialing Events">
                {CREDENTIALING_EVENTS.map(e => (
                  <option key={e.value} value={e.value}>{e.label}</option>
                ))}
              </optgroup>
              <optgroup label="Insurance Events">
                {INSURANCE_EVENTS.map(e => (
                  <option key={e.value} value={e.value}>{e.label}</option>
                ))}
              </optgroup>
            </select>
          </div>

          {/* Application ID (for credentialing events) */}
          {isCredentialingEvent && (
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Application ID (optional)</label>
              <input
                type="text"
                value={applicationId}
                onChange={e => setApplicationId(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white font-mono text-sm"
                placeholder="mxo_app_..."
              />
            </div>
          )}

          {/* Policy ID (for insurance events) */}
          {isInsuranceEvent && (
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Policy ID (optional)</label>
              <input
                type="text"
                value={policyId}
                onChange={e => setPolicyId(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white font-mono text-sm"
                placeholder="mxg_pol_..."
              />
            </div>
          )}

          {/* External Ref */}
          <div>
            <label className="text-sm text-slate-400 mb-1 block">External Reference (optional)</label>
            <input
              type="text"
              value={externalRef}
              onChange={e => setExternalRef(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white font-mono text-sm"
              placeholder="mxo_drv_001"
            />
          </div>

          {/* Custom Payload */}
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Custom Payload (JSON, optional)</label>
            <textarea
              value={customPayload}
              onChange={e => setCustomPayload(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white font-mono text-sm h-24"
              placeholder='{"key": "value"}'
            />
          </div>

          {/* Result */}
          {result && (
            <div className={`p-3 rounded-lg ${result.error ? 'bg-red-500/10 border border-red-500/30' : 'bg-neon-green/10 border border-neon-green/30'}`}>
              <div className="text-sm font-medium mb-2 flex items-center">
                {result.error ? (
                  <><XCircle className="w-4 h-4 mr-2 text-red-500" /> Error</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4 mr-2 text-neon-green" /> Event Fired</>
                )}
              </div>
              <pre className="text-xs text-slate-300 overflow-x-auto">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button onClick={onClose} className="btn-secondary flex-1">
              Close
            </button>
            <button
              onClick={handleFire}
              disabled={loading}
              className="btn-primary flex-1 flex items-center justify-center"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Fire Event
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Webhook Event Row
const WebhookEventRow = ({ event, onView }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/8 transition-colors cursor-pointer"
    onClick={() => onView(event)}
  >
    <div className="flex items-center space-x-3">
      {event.status === 'delivered' || event.status === 'simulated' ? (
        <CheckCircle2 className="w-5 h-5 text-neon-green" />
      ) : event.status === 'failed' ? (
        <XCircle className="w-5 h-5 text-red-500" />
      ) : (
        <Clock className="w-5 h-5 text-yellow-500" />
      )}
      <div>
        <div className="text-white text-sm font-medium">{event.event_type}</div>
        <div className="text-xs text-slate-400">
          {new Date(event.created_at).toLocaleString()}
        </div>
      </div>
    </div>
    <div className="flex items-center space-x-3">
      <div className="text-right text-xs text-slate-400">
        {event.attempts} attempt{event.attempts !== 1 ? 's' : ''}
      </div>
      <StatusBadge status={event.status} />
    </div>
  </motion.div>
);

// Event Detail Modal
const EventDetailModal = ({ event, isOpen, onClose }) => {
  if (!isOpen || !event) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="glass-card p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center">
              <Eye className="w-5 h-5 mr-2 text-neon-cyan" />
              Event Details
            </h3>
            <p className="text-sm text-slate-400 mt-1 font-mono">{event.event_id}</p>
          </div>
          <StatusBadge status={event.status} />
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-xs text-slate-400 mb-1">Event Type</div>
              <div className="text-white text-sm">{event.event_type}</div>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-xs text-slate-400 mb-1">Status</div>
              <div className="text-white text-sm">{event.status}</div>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-xs text-slate-400 mb-1">Attempts</div>
              <div className="text-white text-sm">{event.attempts}</div>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-xs text-slate-400 mb-1">Created</div>
              <div className="text-white text-sm">{new Date(event.created_at).toLocaleString()}</div>
            </div>
          </div>

          <div>
            <div className="text-sm text-slate-400 mb-2">Webhook URL</div>
            <div className="p-3 bg-slate-800 rounded-lg font-mono text-xs text-neon-cyan break-all">
              {event.webhook_url}
            </div>
          </div>

          <div>
            <div className="text-sm text-slate-400 mb-2">Payload</div>
            <pre className="p-3 bg-slate-800 rounded-lg font-mono text-xs text-slate-300 overflow-x-auto max-h-64">
              {JSON.stringify(event.payload, null, 2)}
            </pre>
          </div>

          {event.error && (
            <div>
              <div className="text-sm text-red-400 mb-2">Error</div>
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {event.error}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const PartnerSandboxTester = () => {
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [retryingStuck, setRetryingStuck] = useState(false);
  const [showFireModal, setShowFireModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState('');

  // Fetch partners
  const fetchPartners = async () => {
    try {
      const response = await fetch(`${API_URL}/api/medexova/sandbox/admin/partners?environment=sandbox`);
      const data = await response.json();
      setPartners(data.partners || []);
      
      // Auto-select first ACTIVE partner if available
      if (data.partners?.length > 0 && !selectedPartner) {
        const preferred = data.partners.find((p) => p.is_active) || data.partners[0];
        setSelectedPartner(preferred);
        setApiKeyInput(preferred.api_key || '');
      }
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    }
  };

  // Fetch webhook events
  const fetchWebhookEvents = async () => {
    if (!apiKeyInput) return;
    
    try {
      const response = await fetch(`${API_URL}/api/medexova/sandbox/webhook-events?limit=20`, {
        headers: { 'X-Api-Key': apiKeyInput }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWebhookEvents(data.events || []);
      }
    } catch (error) {
      console.error('Failed to fetch webhook events:', error);
    }
  };

  useEffect(() => {
    fetchPartners().finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (apiKeyInput) {
      fetchWebhookEvents();
    }
  }, [apiKeyInput]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchPartners(), fetchWebhookEvents()]);
    setRefreshing(false);
  };

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(apiKeyInput);
    toast.success('API Key copied to clipboard');
  };

  const handleRetryStuck = async () => {
    if (!apiKeyInput) {
      toast.error('Please enter an API key');
      return;
    }

    setRetryingStuck(true);
    try {
      const response = await fetch(`${API_URL}/api/medexova/sandbox/webhook-events/retry-stuck?older_than_minutes=1`, {
        method: 'POST',
        headers: { 'X-Api-Key': apiKeyInput }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        if (data.scanned === 0) {
          toast.success('No stuck webhook events found');
        } else {
          toast.success(`Re-drove ${data.recovered}/${data.scanned} stuck event(s)`);
        }
        await fetchWebhookEvents();
      } else {
        toast.error(data.error || 'Failed to retry stuck events');
      }
    } catch (error) {
      toast.error('Failed to retry stuck events');
    }
    setRetryingStuck(false);
  };

  const handleSeedData = async () => {
    if (!apiKeyInput) {
      toast.error('Please enter an API key');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/medexova/sandbox/seed-data`, {
        method: 'POST',
        headers: { 'X-Api-Key': apiKeyInput }
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Seeded ${data.applications_created} applications and ${data.policies_created} policies`);
      } else {
        toast.error('Failed to seed data');
      }
    } catch (error) {
      toast.error('Failed to seed data');
    }
  };

  const handleResetData = async () => {
    if (!apiKeyInput) {
      toast.error('Please enter an API key');
      return;
    }

    if (!window.confirm('This will delete ALL sandbox data for this partner. Continue?')) return;

    try {
      const response = await fetch(`${API_URL}/api/medexova/sandbox/reset`, {
        method: 'DELETE',
        headers: { 'X-Api-Key': apiKeyInput }
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Sandbox data reset');
        fetchWebhookEvents();
      } else {
        toast.error('Failed to reset data');
      }
    } catch (error) {
      toast.error('Failed to reset data');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center">
            <Zap className="w-6 h-6 mr-2 text-neon-yellow" />
            Partner API Sandbox
          </h1>
          <p className="text-slate-400 text-sm mt-1">Test webhook events for the partner credentialing integration</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary flex items-center"
            data-testid="refresh-sandbox-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleRetryStuck}
            disabled={retryingStuck}
            className="btn-secondary flex items-center"
            data-testid="retry-stuck-webhooks-btn"
          >
            <RotateCw className={`w-4 h-4 mr-2 ${retryingStuck ? 'animate-spin' : ''}`} />
            Retry Stuck
          </button>
          <button
            onClick={() => setShowFireModal(true)}
            className="btn-primary flex items-center"
            data-testid="fire-event-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Fire Event
          </button>
        </div>
      </div>

      {/* API Key Input */}
      <div className="glass-card p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label className="text-sm text-slate-400 mb-1 block">Partner API Key</label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={apiKeyInput}
                onChange={e => setApiKeyInput(e.target.value)}
                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white font-mono text-sm"
                placeholder="mxo_key_..."
                data-testid="api-key-input"
              />
              <button
                onClick={handleCopyApiKey}
                className="btn-secondary px-3"
                title="Copy API Key"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex space-x-2 pt-5">
            <button
              onClick={handleSeedData}
              className="btn-secondary flex items-center text-sm"
              data-testid="seed-data-btn"
            >
              <Play className="w-4 h-4 mr-1" />
              Seed Data
            </button>
            <button
              onClick={handleResetData}
              className="px-3 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors text-sm flex items-center"
              data-testid="reset-data-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-1" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Partners List */}
      {partners.length > 0 && (
        <div className="glass-card p-4">
          <h2 className="text-lg font-semibold text-white mb-3 flex items-center">
            <Users className="w-5 h-5 mr-2 text-slate-400" />
            Sandbox Partners
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {partners.map(partner => (
              <div
                key={partner.partner_id}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  apiKeyInput === partner.api_key
                    ? 'bg-neon-cyan/10 border-neon-cyan/50'
                    : 'bg-white/5 border-white/10 hover:border-white/20'
                }`}
                onClick={() => setApiKeyInput(partner.api_key || '')}
              >
                <div className="text-white font-medium">{partner.name}</div>
                <div className="text-xs text-slate-400 mt-1">{partner.contact_email}</div>
                <div className="text-xs text-slate-500 font-mono mt-1 truncate">
                  {partner.partner_id}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="glass-card p-4 text-center">
          <Shield className="w-8 h-8 mx-auto mb-2 text-neon-green" />
          <div className="text-white font-medium">Credentialing</div>
          <div className="text-xs text-slate-400 mt-1">6 webhook events</div>
        </div>
        <div className="glass-card p-4 text-center">
          <FileText className="w-8 h-8 mx-auto mb-2 text-neon-purple" />
          <div className="text-white font-medium">Insurance</div>
          <div className="text-xs text-slate-400 mt-1">6 webhook events</div>
        </div>
        <div className="glass-card p-4 text-center">
          <Webhook className="w-8 h-8 mx-auto mb-2 text-neon-cyan" />
          <div className="text-white font-medium">HMAC Signed</div>
          <div className="text-xs text-slate-400 mt-1">SHA256 verification</div>
        </div>
      </div>

      {/* Webhook Events */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center justify-between">
          <span className="flex items-center">
            <Webhook className="w-5 h-5 mr-2 text-neon-cyan" />
            Recent Webhook Events
          </span>
          <span className="text-sm text-slate-400">{webhookEvents.length} events</span>
        </h2>

        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
          {webhookEvents.length > 0 ? (
            webhookEvents.map((event, idx) => (
              <WebhookEventRow
                key={event.event_id || idx}
                event={event}
                onView={setSelectedEvent}
              />
            ))
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Webhook className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No webhook events yet</p>
              <p className="text-sm">Fire an event to see it appear here</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showFireModal && (
          <FireEventModal
            isOpen={showFireModal}
            onClose={() => setShowFireModal(false)}
            apiKey={apiKeyInput}
            onSuccess={fetchWebhookEvents}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedEvent && (
          <EventDetailModal
            event={selectedEvent}
            isOpen={!!selectedEvent}
            onClose={() => setSelectedEvent(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PartnerSandboxTester;
